/**
 * Smart theme selection based on prayer times
 * Between Maghrib (sunset/iftar) and Fajr (dawn/sehri) → dark mode
 * Between Fajr and Maghrib → system/light mode
 * This feels natural for Ramadan users browsing at night
 */
export const useContextAwareTheme = () => {
  const colorMode = useColorMode();
  const { today } = useRamadanTime();

  const isBetweenMaghribAndFajr = computed(() => {
    if (!today.value?.isRamadan) return false;

    const now = new Date();

    // Maghrib (iftar time) - end of day
    const maghribStr = today.value.iftarTime; // "17:11"
    const [maghribH, maghribM] = maghribStr
      .split(":")
      .map(Number)
      .filter((n) => !isNaN(n));
    if (!maghribH || !maghribM) return false;

    const maghrib = new Date();
    maghrib.setHours(maghribH, maghribM, 0);

    // Fajr (sehri end time) - start of day (next day's sehri)
    const fajrStr = today.value.sehriEnds; // "04:30"
    const [fajrH, fajrM] = fajrStr
      .split(":")
      .map(Number)
      .filter((n) => !isNaN(n));
    if (!fajrH || !fajrM) return false;

    const fajr = new Date();
    fajr.setHours(fajrH, fajrM, 0);

    // If fajr is before maghrib (which it always is in a 24h period),
    // we need to check if we're after maghrib OR before fajr
    return now >= maghrib || now < fajr;
  });

  // Apply context-aware theme on mount
  onMounted(() => {
    if (!process.client) return;

    // Only auto-apply if user hasn't explicitly set a preference
    const hasUserPreference = localStorage.getItem("nuxt-color-mode");
    if (!hasUserPreference) {
      // Default to dark between Maghrib and Fajr
      if (isBetweenMaghribAndFajr.value) {
        colorMode.preference = "dark";
      } else {
        colorMode.preference = "system";
      }
    }
  });

  return {
    isBetweenMaghribAndFajr,
  };
};
